import { useState, useEffect, useReducer, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteDealerPortalV1DealerIdApplicationAppId,
  ApiError,
  type DomainDealerPortalApplicationSummary,
} from "@porchpass/api-client";
import { TableFilterProvider, useAuth, useDgaModal } from "src/context";
import { useDealer } from "src/hooks/useDealer";
import Table from "src/components/Table";
import { Row, HeaderRow } from "src/components/Table/Table.styles";
import {
  HeaderCell,
  HeaderSortingCell,
  HeaderStatusCell,
} from "src/components/Table/Cells/shared";
import {
  ApplicationActionsCell,
  LastUpdateCell,
  OriginCell,
  StatusCell,
  TargetDateCell,
} from "src/components/Table/Cells/Applications";
import { NameCell } from "src/components/Table/Cells/shared";
import type { AppMilestoneType } from "src/components/ApplicationStatusBadge";
import FilterControls from "./FilterControls/FilterControls";
import PaginationControls from "./PaginationControls";
import DeleteApplicationDialog from "./DeleteApplicationDialog";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import type { QueryParamsType } from "src/hooks/api/useApiClient";
import { useToast } from "src/components/Toast";
import { useEmployeeCache } from "src/components/DealerGeneratedApplication/hooks/useEmployeeCache";
import {
  API_PREFIX_PATH,
  APPLICATION_STATUSES,
  APPLICATION_STATUS_KEYS,
} from "src/constants";
import { shouldOpenDgaModal } from "src/utils/shouldOpenDgaModal";

interface ApplicationToDelete {
  id: string;
  borrowerName?: string;
}

interface ApplicationItem {
  id: string;
  is_locked?: boolean;
  locked_by_user_id?: string;
  [key: string]: unknown;
}

interface ApplicationsResponse {
  items: ApplicationItem[];
  total: number;
}

interface ApplicationTableProps {
  updateTotalApplicationCount: (count: number) => void;
  allowPagination?: boolean;
  allowFilters?: boolean;
}

export default ({
  updateTotalApplicationCount,
  allowPagination = true,
  allowFilters = true,
}: ApplicationTableProps) => {
  const navigate = useNavigate();
  const { user: _user } = useAuth();
  const { dealer_id } = useDealer();
  const {
    checkingLockAppId,
    openDgaModalWithLock,
    subscribeToApplicationListRefresh,
  } = useDgaModal();
  const { setPendingToast } = useToast();
  const { getEmployeeUser } = useEmployeeCache(dealer_id);
  const [applications, setApplications] = useState<ApplicationsResponse>({
    items: [],
    total: 0,
  });
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [refetchToken, triggerRefetch] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!dealer_id) return;
    return subscribeToApplicationListRefresh(triggerRefetch);
  }, [dealer_id, subscribeToApplicationListRefresh, triggerRefetch]);

  const [appToDelete, setAppToDelete] = useState<ApplicationToDelete | null>(
    null,
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [textSearchField, setTextSearchField] = useState<string | null>(null);
  const [textSearchValue, setTextSearchValue] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<{
    start: string | null;
    end: string | null;
    period?: string;
  }>({ start: null, end: null });
  const [applicationDate, setApplicationDate] = useState<{
    start: string | null;
    end: string | null;
    period?: string;
  }>({
    start: null,
    end: null,
  });
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const latestFetch = useRef(0);
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();

  useEffect(() => {
    const currentFetchId = latestFetch.current + 1;
    latestFetch.current = currentFetchId;
    let isCurrentFetch = true;
    const queryParams: QueryParamsType = {
      limit: pageSize,
      offset,
      sort: "last_updated",
      order: sortDirection,
    };

    if (textSearchField && textSearchValue !== null) {
      queryParams[textSearchField] = textSearchValue.trim();
    }

    if (selectedStatuses.length > 0) {
      queryParams["status"] = selectedStatuses.map(
        (status) =>
          APPLICATION_STATUS_KEYS[
            status as keyof typeof APPLICATION_STATUS_KEYS
          ],
      );
    }

    if (lastUpdated.start && lastUpdated.end) {
      queryParams["last_updated_start"] = lastUpdated.start;
      queryParams["last_updated_end"] = lastUpdated.end;
    } else if (applicationDate.start && applicationDate.end) {
      queryParams["application_date_start"] = applicationDate.start;
      queryParams["application_date_end"] = applicationDate.end;
    }

    const path = `${API_PREFIX_PATH}/${dealer_id}/applications`;
    safeApiCall(() => fetchFromApi(path, { queryParams }))
      .then((data) => {
        const response = data as ApplicationsResponse;
        if (isCurrentFetch && latestFetch.current === currentFetchId) {
          setApplications(response);
          updateTotalApplicationCount(response.total);
        }
      })
      .catch(() => {
        if (isCurrentFetch && latestFetch.current === currentFetchId) return;
      });

    return () => {
      isCurrentFetch = false;
    };
  }, [
    offset,
    pageSize,
    sortDirection,
    textSearchField,
    textSearchValue,
    selectedStatuses,
    applicationDate.start,
    applicationDate.end,
    lastUpdated.start,
    lastUpdated.end,
    refetchToken,
  ]);

  useEffect(() => {
    setOffset(0);
  }, [
    sortDirection,
    textSearchField,
    textSearchValue,
    selectedStatuses,
    applicationDate.start,
    applicationDate.end,
    lastUpdated.start,
    lastUpdated.end,
  ]);

  const handleConfirmDelete = async () => {
    if (!appToDelete || !dealer_id) return;
    const deletedAppId = appToDelete.id;

    let response;
    try {
      response = await deleteDealerPortalV1DealerIdApplicationAppId(
        dealer_id,
        deletedAppId,
      );
    } catch (err) {
      // Network failure / unexpected throw — keep dialog open so user can retry.
      const status = err instanceof ApiError ? err.status : undefined;
      if (status && status >= 500) {
        setPendingToast(
          "There was a problem deleting this draft. Please try again.",
          "error",
        );
        throw err;
      }
      // Non-retryable thrown error: close dialog with a generic toast.
      setPendingToast("There was a problem deleting this draft.", "error");
      return;
    }

    if (response.status === 200) {
      setPendingToast("Draft application deleted.", "success");
      // If we just deleted the last item on a non-first page, step back one page.
      if (applications.items.length === 1 && offset > 0) {
        setOffset(Math.max(0, offset - pageSize));
      } else {
        triggerRefetch();
      }
      return;
    }

    // Error branches from the discriminated union response.
    if (response.status === 404) {
      setPendingToast("This draft has already been deleted.", "success");
      if (applications.items.length === 1 && offset > 0) {
        setOffset(Math.max(0, offset - pageSize));
      } else {
        triggerRefetch();
      }
      return;
    }
    if (response.status === 403) {
      setPendingToast(
        "You don't have permission to delete this draft.",
        "error",
      );
      return;
    }
    if (response.status === 500) {
      setPendingToast(
        "There was a problem deleting this draft. Please try again.",
        "error",
      );
      // Throw to keep the dialog open for retry.
      throw new Error("Server error deleting draft application");
    }
    // 400, 401, or any other non-success: close dialog with generic toast.
    setPendingToast("There was a problem deleting this draft.", "error");
  };

  const Header = () => (
    <HeaderRow>
      <HeaderCell title="Name" />
      <HeaderCell title="Origin" />
      <HeaderSortingCell
        title="Last Updated"
        sortDirection={sortDirection}
        onSort={() =>
          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
        }
      />
      <HeaderCell title="Target Delivery Date" />
      <HeaderCell title="Target Funding Date" />
      <HeaderStatusCell
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
      />
      <HeaderCell />
    </HeaderRow>
  );
  return (
    <>
      {allowFilters && (
        <TableFilterProvider
          value={{
            table: "applications",
            textSearchField,
            setTextSearchField,
            textSearchValue,
            setTextSearchValue,
            role: null,
            setRole: () => {},
            lastUpdated,
            setLastUpdated,
            applicationDate,
            setApplicationDate,
            selectedStatuses,
            setSelectedStatuses,
          }}
        >
          <FilterControls />
        </TableFilterProvider>
      )}
      <Table
        Header={Header}
        rowBuilder={(obj) => {
          const app = obj as unknown as DomainDealerPortalApplicationSummary;
          const handleRowClick = () => {
            if (shouldOpenDgaModal(app.status ?? "", app.created_origin)) {
              openDgaModalWithLock(
                {
                  id: app.id ?? "",
                  is_locked: app.is_locked ?? false,
                  locked_by_user_id: app.locked_by_user_id,
                },
                {
                  onLockStatusResolved: (
                    appId: string,
                    status: { is_locked: boolean; locked_by_user_id?: string },
                  ) => {
                    setApplications((prev) => ({
                      ...prev,
                      items: prev.items.map((item) =>
                        item.id === appId
                          ? {
                              ...item,
                              is_locked: status.is_locked,
                              locked_by_user_id: status.locked_by_user_id,
                            }
                          : item,
                      ),
                    }));
                  },
                },
              );
            } else {
              void navigate(`/${dealer_id}/applications/${app.id ?? ""}`);
            }
          };
          return (
            <Row key={app.id} onClick={handleRowClick}>
              <NameCell
                searchSelection={textSearchField}
                firstName={app.borrower_first_name ?? ""}
                lastName={app.borrower_last_name ?? ""}
                email={app.borrower_email ?? ""}
              />
              <OriginCell origin={app.created_origin} />
              <LastUpdateCell date={app.last_updated_at ?? ""} />
              <TargetDateCell date={app.target_delivery_date ?? ""} />
              <TargetDateCell date={app.target_funding_date ?? ""} />
              <StatusCell
                status={
                  APPLICATION_STATUSES[
                    (app.status ?? "") as keyof typeof APPLICATION_STATUSES
                  ] as AppMilestoneType
                }
                isLocked={app.is_locked ?? false}
                isCheckingLock={checkingLockAppId === app.id}
                lockedByUser={
                  app.is_locked && app.locked_by_user_id
                    ? getEmployeeUser(app.locked_by_user_id)
                    : undefined
                }
              />
              <ApplicationActionsCell
                status={app.status ?? ""}
                isLocked={app.is_locked ?? false}
                isCheckingLock={checkingLockAppId === app.id}
                onDelete={() => {
                  const fullName = [
                    app.borrower_first_name,
                    app.borrower_last_name,
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .trim();
                  setAppToDelete({
                    id: app.id ?? "",
                    borrowerName: fullName || undefined,
                  });
                }}
              />
            </Row>
          );
        }}
        data={applications.items}
      />
      {allowPagination && (
        <PaginationControls
          offset={offset}
          setOffset={setOffset}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={applications.total}
        />
      )}
      <DeleteApplicationDialog
        open={appToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setAppToDelete(null);
        }}
        borrowerName={appToDelete?.borrowerName}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
